import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    TextField,
    MenuItem,
    Stack,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import {
    deleteAffiliateInvoice,
    chargeAffiliateInvoice,
    sendAffiliateInvoiceEmail,
} from '@openapi/invoices/invoices';
import { useGetVenues } from '@openapi/venues/venues';
import { useGetShows } from '@openapi/shows/shows';
import { useGetCommissionGroups } from '@openapi/commission-groups/commission-groups';
import useNotify from '../../hooks/useNotify';
import SortableTableCell from '../../components/atom/SortableTableCell';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import { BACKEND_URL } from '../../../config';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';
// Group rows so consecutive re-runs of the same (show, commissionGroup, date-range) batch
// collapse into a primary row with expandable older batches. Include all scoping IDs so
// unrelated invoices that happen to share the same date range don't fold together.
const getGroupKey = (invoice) =>
    [
        invoice.venueId,
        invoice.showId,
        invoice.commissionGroupId,
        invoice.showDateFrom,
        invoice.showDateTo,
        invoice.direction,
    ].join('|');

function InvoiceHistoryTable({
    invoices,
    loadingInvoices,
    invoicesError,
    reloadInvoices,
    filters,
    setFilters,
    clearFilters,
    sort,
    sortDir,
    setSorting,
}) {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const { showModal } = useModals();
    const [expandedGroups, setExpandedGroups] = useState({});
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
    const [deleteConfirmInvoice, setDeleteConfirmInvoice] = useState(null);
    const [chargingInvoiceId, setChargingInvoiceId] = useState(null);
    const [sendingEmailInvoiceId, setSendingEmailInvoiceId] = useState(null);

    const { data: venueData } = useGetVenues({}, { query: { onError: notifyError } });
    const venues = venueData?.venues || [];

    const { data: showsData } = useGetShows(
        { venueId: filters.venueId || undefined, pageSize: 1000 },
        { query: { enabled: !!filters.venueId, onError: notifyError } },
    );
    const shows = showsData?.shows || [];

    const { data: commissionGroupsData } = useGetCommissionGroups(Number(filters.showId), {
        query: { enabled: !!filters.showId, onError: notifyError },
    });
    const commissionGroups = commissionGroupsData?.commissionGroups || [];

    const updateFilter = (patch) => {
        setFilters((prev) => {
            const next = { ...prev, ...patch };
            // Cascade resets so dependent filters don't reference a no-longer-valid parent.
            if ('venueId' in patch && patch.venueId !== prev.venueId) {
                next.showId = '';
                next.commissionGroupId = '';
            }
            if ('showId' in patch && patch.showId !== prev.showId) {
                next.commissionGroupId = '';
            }
            // Day vs from/to are mutually exclusive: picking one clears the other.
            if ('day' in patch && patch.day) {
                next.from = '';
                next.to = '';
            }
            if (('from' in patch || 'to' in patch) && (patch.from || patch.to)) {
                next.day = '';
            }
            return next;
        });
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const openDeleteConfirm = (invoice) => {
        setDeleteConfirmInvoice(invoice);
    };

    const openChargeConfirm = (invoice) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('invoices.chargeTitle'),
            content: t('invoices.chargeConfirm'),
            confirm: async () => {
                await handleChargeInvoice(invoice);
            },
        });
    };

    const handleDeleteInvoice = async () => {
        if (!deleteConfirmInvoice) return;

        setDeletingInvoiceId(deleteConfirmInvoice.id);
        try {
            await deleteAffiliateInvoice(deleteConfirmInvoice.id);
            notifySuccess(t('invoices.deleteSuccess'));
            setDeleteConfirmInvoice(null);
            await reloadInvoices();
        } catch (error) {
            notifyError(error);
        } finally {
            setDeletingInvoiceId(null);
        }
    };

    const handleChargeInvoice = async (invoice) => {
        setChargingInvoiceId(invoice.id);
        try {
            await chargeAffiliateInvoice(invoice.id);
            notifySuccess(t('invoices.chargeSuccess'));
            await reloadInvoices();
        } catch (error) {
            notifyError(error);
        } finally {
            setChargingInvoiceId(null);
        }
    };

    const handleSendEmail = async (invoice) => {
        setSendingEmailInvoiceId(invoice.id);
        try {
            await sendAffiliateInvoiceEmail(invoice.id);
            notifySuccess(t('invoices.sendEmailSuccess'));
            await reloadInvoices();
        } catch (error) {
            notifyError(error);
        } finally {
            setSendingEmailInvoiceId(null);
        }
    };

    const getInvoiceFilename = (invoice) => `${invoice.invoiceId}.xlsx`;

    const handleDownloadInvoice = async (invoice) => {
        setDownloadingInvoiceId(invoice.id);
        try {
            const isAbsolute = invoice.invoiceFileUrl.startsWith('http');
            if (isAbsolute) {
                // S3 presigned URL — anchor click bypasses CORS. The presigned
                // response carries Content-Disposition: attachment with the
                // correct filename, so we don't need fetch+saveAs.
                const a = document.createElement('a');
                a.href = invoice.invoiceFileUrl;
                a.download = getInvoiceFilename(invoice);
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                // Local /uploads file served by the backend static route.
                // fetch+saveAs preserves the displayed filename even though the
                // static route doesn't set Content-Disposition.
                const url = `${BACKEND_URL}${invoice.invoiceFileUrl}`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Download failed (${response.status})`);
                }
                const blob = await response.blob();
                saveAs(blob, getInvoiceFilename(invoice));
            }
        } catch (error) {
            notifyError(error);
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    const groupedInvoices = [];
    const groupMap = new Map();
    invoices.forEach((invoice) => {
        const key = getGroupKey(invoice);
        if (!groupMap.has(key)) {
            const group = { key, rows: [] };
            groupMap.set(key, group);
            groupedInvoices.push(group);
        }
        groupMap.get(key).rows.push(invoice);
    });

    // Anchor each group's position on its visibleRow so the row that's displayed as primary
    // also drives where the group sits in the sorted list (otherwise a deleted top-of-group
    // row would place the group while a later non-deleted row gets shown).
    const invoiceIndexById = new Map();
    invoices.forEach((invoice, idx) => {
        if (!invoiceIndexById.has(invoice.id)) invoiceIndexById.set(invoice.id, idx);
    });
    groupedInvoices.forEach((group) => {
        const rows = group.rows;
        const visibleRow = rows.find((row) => !row.deletedAt) || rows[0] || null;
        group.visibleRow = visibleRow;
        group.hiddenRows = rows.filter((row) => row.id !== visibleRow?.id);
        group.anchorIndex = visibleRow
            ? (invoiceIndexById.get(visibleRow.id) ?? Number.MAX_SAFE_INTEGER)
            : Number.MAX_SAFE_INTEGER;
    });
    groupedInvoices.sort((a, b) => a.anchorIndex - b.anchorIndex);

    const renderInvoiceRow = (invoice, options = {}) => {
        const { isPrimary = false, group = null } = options;
        const isExpanded = group ? !!expandedGroups[group.key] : false;
        const isChildRow = !isPrimary;
        const rowBackgroundColor = isChildRow ? 'grey.50' : 'common.white';

        return (
            <TableRow key={invoice.id} sx={{ backgroundColor: rowBackgroundColor }}>
                <TableCell sx={{ pl: 2 }}>
                    <Stack spacing={0.25} alignItems="flex-start">
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {invoice.invoiceId}
                        </Typography>
                        {isPrimary && group?.hiddenRows?.length > 0 && (
                            <Button
                                size="small"
                                variant="text"
                                sx={{ minWidth: 0, px: 0, whiteSpace: 'nowrap' }}
                                onClick={() => toggleGroup(group.key)}
                                startIcon={
                                    isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
                                }
                            >
                                {isExpanded
                                    ? t('invoices.hideOlderBatches')
                                    : t('invoices.showOlderBatches', {
                                          count: group.hiddenRows.length,
                                      })}
                            </Button>
                        )}
                    </Stack>
                </TableCell>
                <TableCell>{invoice.showTitle || `#${invoice.showId}`}</TableCell>
                <TableCell>
                    {invoice.commissionGroupName || `#${invoice.commissionGroupId}`}
                </TableCell>
                <TableCell>
                    {new Date(
                        `${String(invoice.showDateFrom).slice(0, 10)}T12:00:00`,
                    ).toLocaleDateString()}{' '}
                    -{' '}
                    {new Date(
                        `${String(invoice.showDateTo).slice(0, 10)}T12:00:00`,
                    ).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ maxWidth: 220, wordBreak: 'break-word' }}>
                    {invoice.sentTo}
                </TableCell>
                <TableCell>{formatDateTimeInVenueTz(invoice.createdAt)}</TableCell>
                <TableCell>
                    <Stack direction="column" spacing={0.5} alignItems="flex-end">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ButtonGroup size="medium" variant="outlined">
                                <Button
                                    disabled={downloadingInvoiceId === invoice.id}
                                    onClick={() => handleDownloadInvoice(invoice)}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {downloadingInvoiceId === invoice.id
                                        ? t('common.loading')
                                        : t('invoices.download')}
                                </Button>
                                <Button
                                    color="error"
                                    disabled={deletingInvoiceId === invoice.id}
                                    onClick={() => openDeleteConfirm(invoice)}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {deletingInvoiceId === invoice.id
                                        ? t('common.loading')
                                        : t('common.delete')}
                                </Button>
                                <Button
                                    disabled={sendingEmailInvoiceId === invoice.id}
                                    onClick={() => handleSendEmail(invoice)}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {sendingEmailInvoiceId === invoice.id
                                        ? t('common.loading')
                                        : invoice.emailSentAt
                                          ? t('invoices.resendEmail')
                                          : t('invoices.sendEmail')}
                                </Button>
                            </ButtonGroup>
                            {invoice.commissionGroup?.stripeCustomerId &&
                                invoice.commissionGroup.billingMethod === 'card' && (
                                    <Button
                                        size="medium"
                                        variant="contained"
                                        disabled={
                                            chargingInvoiceId === invoice.id ||
                                            !!invoice.stripePaymentIntentId
                                        }
                                        onClick={() => openChargeConfirm(invoice)}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {chargingInvoiceId === invoice.id
                                            ? t('common.loading')
                                            : t('invoices.charge')}
                                    </Button>
                                )}
                        </Stack>
                        {invoice.emailSentAt && (
                            <Tooltip title={formatDateTimeInVenueTz(invoice.emailSentAt)}>
                                <Chip
                                    label={t('invoices.emailSent')}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            </Tooltip>
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <>
            <Stack
                spacing={2}
                sx={{
                    // Desktop: bound to the viewport so only the table card scrolls (single
                    // scrollbar) with its header pinned. 256px = fixed chrome above this Stack
                    // (app bar + title + tabs + margins); tune if that changes.
                    // Mobile: the filter panel is tall, so bounding here would starve the
                    // table - instead let the page flow and cap the table height directly
                    // (see the TableContainer maxHeight below).
                    height: { md: 'calc(100dvh - 256px)' },
                    minHeight: { md: 320 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        width: '100%',
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Paper
                        sx={{
                            p: 2,
                            width: { xs: '100%', md: 240 },
                            flexShrink: 0,
                            // The Stack is viewport-bounded, so on desktop let a tall filter
                            // scroll within its own column instead of stretching the row.
                            alignSelf: { md: 'flex-start' },
                            maxHeight: { md: '100%' },
                            overflowY: { md: 'auto' },
                            textAlign: 'left',
                            '& .MuiSelect-select, & input': { textAlign: 'left' },
                        }}
                    >
                        <Stack spacing={2}>
                            <TextField
                                value={filters.venueId}
                                label={t('reports.filters.venue')}
                                select
                                fullWidth
                                onChange={(e) => updateFilter({ venueId: e.target.value })}
                                size="small"
                            >
                                <MenuItem value="">{t('common.all')}</MenuItem>
                                {venues.map((v) => (
                                    <MenuItem key={v.id} value={v.id}>
                                        {v.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                label={t('reports.filters.show')}
                                value={filters.showId}
                                onChange={(e) => updateFilter({ showId: e.target.value })}
                                size="small"
                                disabled={!filters.venueId}
                            >
                                <MenuItem value="">{t('common.all')}</MenuItem>
                                {shows.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                label={t('reports.filters.commissionGroup')}
                                value={filters.commissionGroupId}
                                onChange={(e) =>
                                    updateFilter({ commissionGroupId: e.target.value })
                                }
                                size="small"
                                disabled={!filters.showId}
                            >
                                <MenuItem value="">{t('common.all')}</MenuItem>
                                {commissionGroups.map((cg) => (
                                    <MenuItem key={cg.id} value={cg.id}>
                                        {cg.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                label={t('invoices.day')}
                                type="date"
                                value={filters.day}
                                onChange={(e) => updateFilter({ day: e.target.value })}
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                fullWidth
                                label={t('reports.filters.dateFrom')}
                                type="date"
                                value={filters.from}
                                onChange={(e) => updateFilter({ from: e.target.value })}
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                fullWidth
                                label={t('reports.filters.dateTo')}
                                type="date"
                                value={filters.to}
                                onChange={(e) => updateFilter({ to: e.target.value })}
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <Button variant="outlined" size="small" onClick={clearFilters}>
                                {t('common.clear')}
                            </Button>
                        </Stack>
                    </Paper>

                    <Paper
                        sx={{
                            p: 2,
                            flex: 1,
                            minWidth: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }}
                    >
                        {loadingInvoices && (
                            <Box display="flex" justifyContent="center" p={2}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {!loadingInvoices && invoicesError && (
                            <Alert severity="error">{t('errors.somethingWentWrong')}</Alert>
                        )}

                        {!loadingInvoices && !invoicesError && invoices.length === 0 && (
                            <Typography color="text.secondary">
                                {t('invoices.noInvoices')}
                            </Typography>
                        )}

                        {!loadingInvoices && !invoicesError && groupedInvoices.length > 0 && (
                            <TableContainer
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'auto',
                                    // Mobile has no bounded parent, so give the table a real
                                    // height of its own; desktop fills the bounded Stack.
                                    maxHeight: { xs: '60dvh', md: 'none' },
                                }}
                            >
                                <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow>
                                            <SortableTableCell
                                                arrow={sort === 'id' ? sortDir : ''}
                                                onClick={() => setSorting('id')}
                                            >
                                                {t('invoices.invoiceNumber')}
                                            </SortableTableCell>
                                            <TableCell>{t('reports.filters.show')}</TableCell>
                                            <TableCell>
                                                {t('reports.filters.commissionGroup')}
                                            </TableCell>
                                            <SortableTableCell
                                                arrow={sort === 'show_date_from' ? sortDir : ''}
                                                onClick={() => setSorting('show_date_from')}
                                            >
                                                {t('reports.dateRange')}
                                            </SortableTableCell>
                                            <TableCell>{t('invoices.sentTo')}</TableCell>
                                            <SortableTableCell
                                                arrow={sort === 'created_at' ? sortDir : ''}
                                                onClick={() => setSorting('created_at')}
                                            >
                                                {t('invoices.createdAt')}
                                            </SortableTableCell>
                                            <TableCell>{t('common.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groupedInvoices.map((group) => (
                                            <React.Fragment key={group.key}>
                                                {group.visibleRow &&
                                                    renderInvoiceRow(group.visibleRow, {
                                                        isPrimary: true,
                                                        group,
                                                    })}
                                                {expandedGroups[group.key] &&
                                                    group.hiddenRows.map((invoice) =>
                                                        renderInvoiceRow(invoice),
                                                    )}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Box>
            </Stack>

            <Dialog
                open={!!deleteConfirmInvoice}
                onClose={() => setDeleteConfirmInvoice(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('common.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('invoices.deleteConfirm')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmInvoice(null)} variant="outlined">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteInvoice}
                        color="error"
                        variant="contained"
                        disabled={
                            !deleteConfirmInvoice || deletingInvoiceId === deleteConfirmInvoice.id
                        }
                    >
                        {deleteConfirmInvoice && deletingInvoiceId === deleteConfirmInvoice.id
                            ? t('common.loading')
                            : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default InvoiceHistoryTable;
